import axios from 'axios'

export interface ReferralCode {
  referral_code_id: string
  user_id: string
  code: string
  is_active: boolean
  total_referrals: number
  rewards_earned: number
  created_at: string
  user_email?: string
  user_name?: string
}

export interface ReferralStats {
  total_referrals: number
  successful_referrals: number
  rewards_earned: number
  pending_rewards: number
  conversion_rate: number
}

export interface Referral {
  referral_id: string
  referrer_id: string
  referred_email: string | null
  referred_user_id: string | null
  status: 'pending' | 'completed' | 'expired'
  reward_amount: number
  created_at: string
  completed_at: string | null
  referrer_email?: string
  referrer_name?: string
}

export interface ReferralConfig {
  config_id: string
  reward_amount: number
  minimum_order_amount: number
  referral_code_expiry_days: number
  max_referrals_per_user: number
  is_active: boolean
  created_at: string
  updated_at: string
}

const API_BASE_URL = '/api'

const referralApi = axios.create({
  baseURL: `${API_BASE_URL}/referrals`,
  headers: {
    'Content-Type': 'application/json',
  },
})

referralApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

referralApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const referralService = {
  // Configuration
  async getConfig(): Promise<{ success: boolean; data: ReferralConfig }> {
    const response = await referralApi.get('/config')
    return response.data
  },

  async updateConfig(data: Partial<ReferralConfig>): Promise<{ success: boolean; data: ReferralConfig }> {
    const response = await referralApi.put('/config', data)
    return response.data
  },

  // Analytics
  async getStats(): Promise<{ success: boolean; data: ReferralStats }> {
    const response = await referralApi.get('/admin/stats')
    return response.data
  },

  async getTopReferrers(limit: number = 10): Promise<{ success: boolean; data: { user_id: string; email: string; name: string; total_referrals: number; rewards_earned: number }[] }> {
    const response = await referralApi.get(`/admin/top-referrers?limit=${limit}`)
    return response.data
  },

  async getConversionRate(): Promise<{ success: boolean; data: { rate: number; total_invited: number; total_converted: number } }> {
    const response = await referralApi.get('/admin/conversion-rate')
    return response.data
  },

  // Code management
  async getAllCodes(filters?: { is_active?: boolean; page?: number; limit?: number }): Promise<{ success: boolean; data: ReferralCode[]; total: number }> {
    const params = new URLSearchParams()
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active))
    if (filters?.page) params.append('page', String(filters.page))
    if (filters?.limit) params.append('limit', String(filters.limit))
    const response = await referralApi.get(`/admin/codes?${params.toString()}`)
    return response.data
  },

  async disableCode(codeId: string): Promise<{ success: boolean; message: string }> {
    const response = await referralApi.patch(`/admin/codes/${codeId}/disable`)
    return response.data
  },

  async generateBulkCodes(count: number): Promise<{ success: boolean; data: ReferralCode[]; message: string }> {
    const response = await referralApi.post('/admin/codes/bulk', { count })
    return response.data
  },

  // Referrals list
  async getAllReferrals(page: number = 1, limit: number = 20): Promise<{ success: boolean; data: Referral[]; total: number }> {
    const response = await referralApi.get(`/admin/referrals?page=${page}&limit=${limit}`)
    return response.data
  },
}

export default referralService
